import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import { usePaginationReducer, useOrderableTableReducer } from '@sopranodesign/pulse-components';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { StateLevel } from '../';
import {
  deleteResource as apiDeleteResource,
  saveSharePointTags,
  createSharePointTag,
  fetchSharePointTags,
  fetchSharePointAvailableTags
} from '../../api';
import WarningMessageBody from '../WarningMessageBody';
import { useTagManager } from '../useTagManager';
import { useBulkSelection } from '../useBulkSelection';
import { runBulkAction, describeBulkResult } from '../bulkActionUtils';
import BulkActionsBar from '../BulkActionsBar';
import TagModal from '../TagModal';

const Resource = ({ site, folder, state }) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const {
    crawlResources,
    setActiveModal,
    setCrawlResources,
    setSelectedResource
  } = state;

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const order = useOrderableTableReducer({ id: 'resources-sharepoint' });
  const pagination = usePaginationReducer({ id: 'resources-sharepoint' });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/sharepoint/groups/available'],
    queryFn: fetchSharePointAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_sharepoint_files',
    createTagFn: createSharePointTag
  });

  const queryKey = useMemo(
    () => [
      `generative-ai/sharepoint/site/${site.id}/folder/${folder.id}/resource`,
      { ...pagination.params, ...order.params }
    ],
    [
      site.id,
      folder.id,
      order.params,
      pagination.params
    ]
  );

  const refetchResources = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [`generative-ai/sharepoint/site/${site.id}/folder/${folder.id}/resource`]
    });
  }, [queryClient, site.id, folder.id]);

  const enabled = Boolean(site.id) && Boolean(folder.id);

  const {
    error,
    isLoading,
    data: resources
  } = useQuery({
    queryKey,
    enabled,
    refetchInterval: crawlResources ? 5000 : false
  });

  useEffect(() => {
    const hasPending = resources?.has_pending === true;

    if (hasPending && !crawlResources) {
      setCrawlResources(true);
    } else if (!hasPending && crawlResources) {
      setCrawlResources(false);
    }
  }, [crawlResources, setCrawlResources, resources?.has_pending]);

  const handleTagModal = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('tagsModal');
  }, [setActiveModal, setSelectedResource]);

  const handleDeleteResource = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('deleteResource');
  }, [setActiveModal, setSelectedResource]);

  const handleRecrawlResource = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('recrawlResource');
  }, [setActiveModal, setSelectedResource]);

  const meta = resources?.meta ?? {};
  const data = resources?.data ?? [];
  const pageIds = data.map(resource => resource.id);
  const findResource = (id) => data.find(resource => resource.id === id);

  const handleBulkDelete = () => {
    const eligible = bulk.selectedIds
      .map(findResource)
      .filter(resource => resource && resource.status !== 'Manually Deleted');
    if (eligible.length === 0) {
      showError('None of the selected files can be deleted right now.');
      return;
    }
    setBulkModal({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete ${eligible.length} selected file${eligible.length > 1 ? 's' : ''}?`}
          contentBody="All synced data will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(eligible, (resource) => apiDeleteResource(resource.file_id));
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult({ succeeded: result.succeeded, failed: result.failed }, 'delete requested', 'file')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request delete for ${result.failed.length} file${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchResources();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findResource(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected files are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findResource(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchSharePointTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveSharePointTags({ resourceId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'file')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} file${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchResources();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading resource...</Paragraph>
      </Flex>
    );
  }

  if (error) {
    return (
      <p style={{ color: 'red' }}>
        Error fetching resources: {error.message || 'Unknown error occurred'}
      </p>
    );
  }

  return (
    <Flex col>
      {data.length === 0 ? (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>
              No documents have been added yet, please make sure folder is not empty in SharePoint.
            </Paragraph>
          </Flex>
        </Box>
      ) : (
        <>
          <PermissionFilter can="edit generative ai resource">
            <BulkActionsBar
              count={bulk.count}
              onClear={bulk.clear}
              actions={[
                <PermissionFilter can="view generative ai tag" key="bulk-tag">
                  <PermissionFilter can="edit generative ai tag">
                    <Button
                      icon="tag"
                      variant="secondary"
                      onClick={openBulkTagsModal}
                    >
                      Tags
                    </Button>
                  </PermissionFilter>
                </PermissionFilter>,
                <Button
                  key="bulk-delete"
                  variant="secondary"
                  icon="trash"
                  colour="danger"
                  loading={bulkLoading}
                  onClick={handleBulkDelete}
                >
                  Delete
                </Button>
              ]}
            />
          </PermissionFilter>
          <Flex border xScroll>
            <Table loading={isLoading}>
              <TableHead>
                <OrderableTableRow
                  rows={[
                    ...(canBulkSelect ? [{
                      label: (
                        <Checkbox
                          aria-label="Select all files"
                          checked={bulk.isAllSelected(pageIds)}
                          onChange={() => bulk.toggleAll(pageIds)}
                        />
                      )
                    }] : []),
                    { label: 'Name', sort: 'name' },
                    { label: 'Type', sort: 'type' },
                    { label: 'Status', sort: 'status' },
                    ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                  ]}
                  {...order.props}
                />
              </TableHead>
              <TableBody>
                {data.map((resource) => (
                  <TableRow key={resource.id}>
                    {canBulkSelect && (
                      <TableCell>
                        <Checkbox
                          aria-label={`Select ${resource.name}`}
                          checked={bulk.isSelected(resource.id)}
                          onChange={() => bulk.toggle(resource.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{resource.name}</TableCell>
                    <TableCell>{resource.type}</TableCell>
                    <TableCell>
                      <Flex center>
                        <StateLevel level={resource.status} />
                        <span>{resource.status}</span>
                      </Flex>
                    </TableCell>
                    {canViewTag && (
                      <TableCell>
                        {resource.groups?.length
                          ? resource.groups.map(group => group.name).join(', ')
                          : 'No Tags'}
                      </TableCell>
                    )}
                    {((canViewTag && canEditTag) || canEditResource) && (
                      <TableActions>
                        <PermissionFilter can="view generative ai tag">
                          <PermissionFilter can="edit generative ai tag">
                            {resource.status === 'Success' && (
                              <Button
                                icon="tag"
                                onClick={() => handleTagModal(resource)}
                              >
                                Tags
                              </Button>
                            )}
                          </PermissionFilter>
                        </PermissionFilter>
                        <PermissionFilter can="edit generative ai resource">
                          {resource.status !== 'Manually Deleted' && (
                            <Button
                              variant="secondary"
                              icon="trash"
                              colour="danger"
                              onClick={() => handleDeleteResource(resource)}
                            >
                              Delete
                            </Button>
                          )}
                          {resource.status === 'Manually Deleted' && (
                            <Button
                              icon="download"
                              onClick={() => handleRecrawlResource(resource)}
                            >
                              Enable and Crawl
                            </Button>
                          )}
                        </PermissionFilter>
                      </TableActions>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Flex>
          {meta && <Pagination pagination={meta} {...pagination.props} />}

          <TagModal
            isVisible={isBulkTagsModalVisible}
            header={`Tag ${bulk.count} Files`}
            onCancel={() => {
              setIsBulkTagsModalVisible(false);
              setSelectedTags([]);
            }}
            onConfirm={handleBulkTagConfirm}
            tags={tags}
            selectedTags={selectedTags}
            handleTagChange={handleTagChange}
          />

          {bulkModal && (
            <Modal
              header={bulkModal.header}
              body={bulkModal.body}
              confirmText={bulkModal.confirmText}
              cancelText="Cancel"
              handleCancel={() => setBulkModal(null)}
              handleConfirm={bulkModal.handleConfirm}
              loading={bulkLoading}
            />
          )}
        </>
      )}
    </Flex>
  );
};

Resource.propTypes = {
  site: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  folder: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  state: PropTypes.shape({
    crawlResources: PropTypes.bool.isRequired,
    setSelectedResource: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setSelectedFolder: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    setCrawlResources: PropTypes.func.isRequired
  }).isRequired
};

export default Resource;